import React, { useMemo, lazy } from 'react';
import { Typography, ListItemIcon, ListItemText } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import * as workoutSessionApi from '../../services/workoutSessionApi';

const WorkoutSessionForm = lazy(() => import('./WorkoutSessionForm'));
const WorkoutSessionView = lazy(() => import('./WorkoutSessionView'));

const WorkoutSessionListItemContent = ({ item, config }) => {
  const { getDisplayName } = config;
  const displayName = getDisplayName(item);
  const dateString = useMemo(() => {
    const date = item.start_time || item.created_at;
    return date ? new Date(date).toLocaleDateString([], { year: '2-digit', month: '2-digit', day: '2-digit' }) : '';
  }, [item]);
  return (
    <>
      <ListItemIcon sx={{ minWidth: 36 }}>
        <HistoryIcon />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="body2" noWrap sx={{ display: 'flex', alignItems: 'center' }}>
            {dateString && <Typography component="span" variant="caption" sx={{ mr: 1, color: 'text.secondary', fontFamily: 'monospace' }}>{dateString}</Typography>}
            <Typography component="span" noWrap>{displayName}</Typography>
          </Typography>
        }
      />
    </>
  );
};

// Helper to remove measurements that have no value before sending to API
const cleanSessionData = (data) => {
  if (!data) return data;
  const clean = JSON.parse(JSON.stringify(data)); // Deep clone

  if (clean.activities && Array.isArray(clean.activities)) {
    clean.activities.forEach(activity => {
      if (activity.actions && Array.isArray(activity.actions)) {
        activity.actions.forEach(action => {
          if (action.measurements && Array.isArray(action.measurements)) {
            // FILTER: Keep only measurements that have a valid numeric value
            action.measurements = action.measurements.filter(m => 
              m.value !== null && 
              m.value !== '' && 
              m.value !== undefined &&
              !isNaN(parseFloat(m.value))
            );
          }
        });
      }
    });
  }
  return clean;
};

export const workoutSessionConfig = {
  id: 'workoutLogs',
  resourceType: 'workout_session',
  singularName: 'Workout Session',
  pluralName: 'Workout Sessions',
  addButtonLabel: 'Log Workout Session',
  icon: HistoryIcon,
  searchable: true,
  usePreviewModal: true,
  createChainToEdit: true,
  modalSize: 'lg',
  getDisplayName: (item) => item.name || 'Unnamed Session',
  renderListItemContent: WorkoutSessionListItemContent,
  sortOptions: [
    {
      label: 'Sort by Date',
      value: 'date',
      sorter: (a, b) => {
        const dateB = new Date(b.start_time || b.created_at);
        const dateA = new Date(a.start_time || a.created_at);
        return dateB - dateA;
      },
    },
    {
      label: 'Sort by Name',
      value: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      label: 'Sort by Status',
      value: 'status',
      sorter: (a, b) => {
        const statusA = a.status || '';
        const statusB = b.status || '';
        if (statusA !== statusB) {
          return statusA.localeCompare(statusB);
        }
        const dateB = new Date(b.start_time || b.created_at);
        const dateA = new Date(a.start_time || a.created_at);
        return dateB - dateA;
      },
    },
  ],
  getInitialState: (data) => ({
    id: data?.id || null,
    name: data?.name || '',
    status: data?.status || (data?.id ? 'PLANNED' : 'IN_PROGRESS'),
    start_time: data?.start_time || (data?.id ? null : new Date().toISOString()),
    end_time: data?.end_time || null,
    user_notes: data?.user_notes || '',
    location_id: data?.location_id || null,
    workout_plan_id: data?.workout_plan_id || null,
    activities: data?.activities || [],
    chat_session_id: data?.chat_session_id || null,
    date_id: data?.date_id || null,
    created_at: data?.created_at || null,
    shadows_id: data?.shadows_id || null,
    user_id: data?.user_id,
    _isPartial: data?._isPartial || false,
  }),
  api: {
    getAll: () => workoutSessionApi.getWorkoutSessions({
      fields: 'id,name,status,start_time,end_time,created_at,workout_plan_id,location_id'
    }),
    // Clean data before creating
    create: (data) => workoutSessionApi.createWorkoutSession(cleanSessionData(data)),
    // Clean data before updating
    update: (id, data) => workoutSessionApi.updateWorkoutSession(id, cleanSessionData(data)),
    
    delete: workoutSessionApi.deleteWorkoutSession,
    getById: workoutSessionApi.getWorkoutSessionById,
  },
  getAdditionalProps: async () => {
    return {};
  },
  FormComponent: WorkoutSessionForm,
  ViewComponent: WorkoutSessionView,
};